import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import MaskedLines from "@/components/landing/MaskedLines";

const FABRIC =
  "https://static.prod-images.emergentagent.com/jobs/3d06aecc-7c40-4cf7-8c1b-6c21e0f317e1/images/97b02a7e611967ec8cf8a293af97619479b3289da7ba7440ed70079eecac0e6d.png";

const features = [
  {
    n: "01",
    title: "Long-staple cotton",
    body: "Grown along the Namoi Valley. Spun into a 480-gram loop-back jersey that softens with every wash but never thins.",
  },
  {
    n: "02",
    title: "Garment-dyed in small batches",
    body: "Each piece is dyed after it is sewn, giving the fabric its lived-in depth and one-of-one character.",
  },
  {
    n: "03",
    title: "Cut & sewn in Katoomba",
    body: "A nine-person workshop in the heart of the Blue Mountains. Slow patterns, hand-finished seams.",
  },
  {
    n: "04",
    title: "Designed to outlive trends",
    body: "Heavy-weight panels, reinforced ribs, taped shoulders. Built to be worn for a decade — then mended.",
  },
];

export default function Features() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section
      ref={ref}
      id="features"
      data-testid="features-section"
      className="relative bg-[#0a0f1d] py-32 md:py-44 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-8 lg:gap-12">
          {/* Sticky image */}
          <div className="col-span-12 lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <span className="label-overline">03 · Craft</span>
              <MaskedLines
                as="h3"
                className="mt-6 font-serif-display text-white text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05]"
                lines={[
                  "The detail you'll",
                  <em key="l2" className="italic text-[#D4AF37]">never see.</em>,
                ]}
              />
              <div className="mt-10 relative h-[420px] md:h-[520px] overflow-hidden border border-white/5">
                <motion.img
                  src={FABRIC}
                  alt="Premium cotton fabric"
                  style={{ y: imgY, scale: imgScale }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0f1d]/60" />
                <div className="absolute bottom-5 left-5 wordmark-plate px-4 py-2">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                    480 gsm · Australian cotton
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Feature list */}
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex flex-col">
            {features.map((f, i) => (
              <motion.div
                key={i}
                data-testid={`feature-${i}`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.08 }}
                className="group relative border-t border-white/10 py-10 md:py-12 first:border-t-0 last:border-b last:border-white/10"
              >
                <div className="flex items-start gap-6 md:gap-10">
                  <div className="font-serif-display text-[#D4AF37] text-3xl md:text-4xl italic font-light">
                    {f.n}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-serif-display text-2xl md:text-[32px] font-light text-white leading-tight transition-colors group-hover:text-[#D4AF37]">
                      {f.title}
                    </h4>
                    <p className="mt-4 text-white/65 leading-[1.85] text-[15px] max-w-[520px]">
                      {f.body}
                    </p>
                  </div>
                </div>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="text-[#D4AF37] text-2xl">→</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
